(() => {
  'use strict';

  const VARIANT_ID = '47798284517652';
  const UNIT_PRICE = 40;
  const ALLOWED_HOSTS = new Set(['shop.mclaughlingolf.com', '40977b.myshopify.com']);
  const ATTRIBUTION_KEYS = [
    'utm_id',
    'utm_source',
    'utm_medium',
    'utm_campaign',
    'utm_term',
    'utm_content',
    'utm_source_platform',
    'gclid',
    'fbclid',
  ];

  const parseQuantity = (value, maximum) => {
    const normalized = String(value);
    if (!/^[1-9]\d*$/.test(normalized)) return null;
    const quantity = Number(normalized);
    return Number.isSafeInteger(quantity) && quantity <= maximum ? quantity : null;
  };

  const money = (quantity, unitPrice = UNIT_PRICE) => `$${quantity * unitPrice}`;

  const buildCheckoutUrl = (rawUrl, quantity, maximum) => {
    try {
      if (!parseQuantity(quantity, maximum)) return null;
      const url = new URL(rawUrl, window.location.origin);
      const validPath = new RegExp(`^/cart/${VARIANT_ID}:[1-9]\\d*$`).test(url.pathname);
      const validQuery = Array.from(url.searchParams.keys()).every((key) => key === 'skip_shop_pay' || ATTRIBUTION_KEYS.includes(key));
      if (
        url.protocol !== 'https:' ||
        !ALLOWED_HOSTS.has(url.hostname.toLowerCase()) ||
        url.username ||
        url.password ||
        url.port ||
        url.hash ||
        !validPath ||
        !validQuery ||
        url.searchParams.get('skip_shop_pay') !== 'true'
      ) return null;

      url.pathname = `/cart/${VARIANT_ID}:${quantity}`;
      url.search = '';
      url.searchParams.set('skip_shop_pay', 'true');
      const landing = new URLSearchParams(window.location.search);
      for (const key of ATTRIBUTION_KEYS) {
        const value = landing.get(key)?.trim();
        if (value) url.searchParams.set(key, value.slice(0, 200));
      }
      return url.toString();
    } catch (_error) {
      return null;
    }
  };

  const initializeQuantity = (control) => {
    if (!(control instanceof HTMLElement) || control.dataset.initialized === 'true') return;
    control.dataset.initialized = 'true';

    const input = control.querySelector('[data-quantity-input]');
    const decrease = control.querySelector('[data-quantity-decrease]');
    const increase = control.querySelector('[data-quantity-increase]');
    const status = control.querySelector('[data-quantity-status]');
    const maximum = Number(control.dataset.maximum);
    const unitPrice = Number(control.dataset.unitCents || control.closest('[data-journey-line]')?.dataset.unitCents || 4000) / 100;
    const line = control.closest('[data-journey-line]');
    const lineTotal = line?.querySelector('[data-line-total]');
    const checkoutLinks = Array.from(document.querySelectorAll('[data-journey-checkout]'));
    const checkoutBases = new Map(checkoutLinks.map((link) => [link, link.getAttribute('href')]));
    let lastValid = Number(input?.value) || 1;

    const update = (requested, announce = true) => {
      const quantity = parseQuantity(requested, maximum);
      if (!input || !quantity) return false;

      lastValid = quantity;
      input.value = String(quantity);
      input.setAttribute('aria-valuenow', String(quantity));
      input.removeAttribute('aria-invalid');
      if (decrease) decrease.disabled = quantity <= 1;
      if (increase) increase.disabled = quantity >= maximum;
      if (lineTotal) lineTotal.textContent = money(quantity, unitPrice);
      const cartSubtotal = document.querySelector('[data-cart-subtotal]');
      if (cartSubtotal) {
        const subtotal = Array.from(document.querySelectorAll('[data-journey-line]')).reduce((sum, cartLine) => {
          const lineInput = cartLine.querySelector('[data-quantity-input]');
          const lineQuantity = Number(lineInput?.value || 0);
          const lineUnitPrice = Number(cartLine.dataset.unitCents || 0) / 100;
          return sum + (lineQuantity * lineUnitPrice);
        }, 0);
        cartSubtotal.textContent = `$${subtotal}`;
      }
      document.querySelectorAll('[data-journey-total]').forEach((node) => { node.textContent = money(quantity); });

      for (const link of checkoutLinks) {
        const nextUrl = buildCheckoutUrl(checkoutBases.get(link), quantity, maximum);
        if (nextUrl) {
          link.href = nextUrl;
          link.removeAttribute('aria-disabled');
        } else {
          link.href = '#';
          link.setAttribute('aria-disabled', 'true');
        }
      }

      if (announce && status) {
        status.textContent = `${quantity} ${quantity === 1 ? 'hat' : 'hats'} selected. Total ${money(quantity, unitPrice)}.`;
      }
      return true;
    };

    const reject = () => {
      if (!input) return;
      input.value = String(lastValid);
      input.setAttribute('aria-valuenow', String(lastValid));
      input.setAttribute('aria-invalid', 'true');
      if (status) status.textContent = `Enter a whole number from 1 to ${maximum}. Quantity remains ${lastValid}.`;
    };

    if (!Number.isSafeInteger(maximum) || maximum < 1 || !update(lastValid, false)) {
      if (input) input.disabled = true;
      if (decrease) decrease.disabled = true;
      if (increase) increase.disabled = true;
      if (status) status.textContent = 'Quantity editing is unavailable until sellable inventory can be confirmed.';
      for (const link of checkoutLinks) {
        link.href = '#';
        link.setAttribute('aria-disabled', 'true');
      }
      return;
    }

    input.addEventListener('keydown', (event) => {
      if (['e', 'E', '+', '-', '.'].includes(event.key)) event.preventDefault();
    });
    input.addEventListener('input', () => {
      if (input.value === '') return;
      if (!update(input.value)) reject();
    });
    input.addEventListener('change', () => { if (!update(input.value)) reject(); });
    input.addEventListener('blur', () => { if (!update(input.value)) reject(); });
    decrease?.addEventListener('click', () => update(lastValid - 1));
    increase?.addEventListener('click', () => update(lastValid + 1));
  };

  const initialize = (root = document) => {
    root.querySelectorAll?.('[data-journey-quantity]').forEach(initializeQuantity);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initialize(), { once: true });
  } else {
    initialize();
  }
  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
})();
